import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, message, Modal, Row, Select, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import BooleanTag from '@/components/app/BooleanTag';
import { CompanyTicketClassDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import CreateBtn from '@/components/app/CreateBtn';

const PAGE_TITLE = 'smsTemplatesPage_Title';

const columns: IColumnType<CompanyTicketClassDto>[] = [
  {
    title: 'smsTemplatesPage_Name',
    dataIndex: 'name',
    ellipsis: true,
    defaultSortOrder: 'ascend',
    width: 300,
  },
  {
    title: 'smsTemplatesPage_CultureCode',
    dataIndex: 'cultureCode',
    width: 120,
    sorter: true,
    ellipsis: true,
  },
  {
    title: 'smsTemplatesPage_Content',
    dataIndex: 'content',
    ellipsis: true,
    render: v => <Tooltip title={v}>{v}</Tooltip>,
  },
  {
    title: 'base.Enabled',
    dataIndex: 'enabled',
    width: 100,
    render: v => <BooleanTag value={v} />,
  },
];

const SmsTemplateTab = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [smsTemplateNames, setSmsTemplateNames] = useState<string[]>([]);

  const getSmsNames = () => {
    apiSdk.SmsTemplateService.getSmsNames()
      .then(response => {
        setSmsTemplateNames(response);
      })
      .catch(error => {
        message.error(error);
      });
  };
  useEffect(() => {
    getSmsNames();
  }, []);
  const createEntity = values => {
    setLoading(true);
    apiSdk.SmsTemplateService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.SmsTemplateService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const filters: Array<IFilterItem> = React.useMemo(() => {
    return [
      {
        type: 'LIST',
        name: 'name',
        label: 'smsTemplatesPage_Name',
        listData: smsTemplateNames.map(sms => {
          return { label: sms, value: sms };
        }),
      },
    ] as Array<IFilterItem>;
  }, [smsTemplateNames]);
  return (
    <div>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          searchBtnAlignLeft
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/notifications/sms-templates/get-sms-templates"
          bordered
          order_by="name"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
            });
          }}
          allowDelete={true}
          delete_url="/notifications/sms-templates/delete"
          filters={filters}
          extraBtns={
            <CreateBtn
              onClick={() => {
                setIsNewForm(true);
                setFormVisible(true);
                form.resetFields();
              }}
            />
          }
        ></ZTable>
        {formVisible ? (
          <Modal
            visible={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={isNewForm ? t('smsTemplatesPage_CreatTemplate') : t('smsTemplatesPage_UpdateTemplaet')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Form
              initialValues={{ cultureCode: 'en', enabled: true }}
              form={form}
              size="middle"
              layout="horizontal"
              labelAlign="right"
              labelCol={{ md: 10 }}
            >
              <Form.Item name="id" className="hidden">
                <Input type="hidden" />
              </Form.Item>

              <Row gutter={ROW_GUTTER}>
                <Col md={24}>
                  <Form.Item name="name" label={t('smsTemplatesPage_Name')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <Select placeholder={t('smsTemplatesPage_Name')}>
                      {smsTemplateNames.map(k => (
                        <Select.Option key={k} value={k || ''}>
                          {k}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="cultureCode" label={t('smsTemplatesPage_CultureCode')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <Select>
                      <Select.Option value="en">English</Select.Option>
                      <Select.Option value="fr">Français</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item label={t('smsTemplatesPage_Content')} name="content" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <Input.TextArea />
                  </Form.Item>
                  <Form.Item label="   " colon={false} name="enabled" valuePropName="checked">
                    <Checkbox>{t('base.Enabled')} </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>
        ) : null}
      </div>
    </div>
  );
};
export default SmsTemplateTab;
